import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Search, CheckCircle, Cancel, Visibility } from "@mui/icons-material";
import {
  useReadManyPaymentReceiptsQuery,
  useApprovePaymentReceiptMutation,
  useRejectPaymentReceiptMutation,
} from "api/endpoints/payment-receipt";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";

// Removed - using centralized function from common/contracts

export const PaymentReceiptListComponent = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [viewingReceipt, setViewingReceipt] = useState<string>("");

  // Confirmation dialog states
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "تایید",
    confirmColor: "primary",
    onConfirm: () => {},
  });

  const {
    data: receiptsList,
    isLoading,
    error,
    refetch,
  } = useReadManyPaymentReceiptsQuery({
    query: {
      page: String(page + 1),
      limit: String(rowsPerPage),
    },
  });

  const [approveReceipt] = useApprovePaymentReceiptMutation();
  const [rejectReceipt] = useRejectPaymentReceiptMutation();

  const receipts = receiptsList?.receipts || [];
  const totalCount = receiptsList?.count || 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showConfirmationDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info",
    onConfirm: () => void
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      confirmColor,
      onConfirm,
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleApprove = (receiptId: string) => {
    showConfirmationDialog(
      "تایید فیش واریزی",
      "آیا از تایید این فیش واریزی اطمینان دارید؟",
      "تایید",
      "success",
      async () => {
        try {
          await approveReceipt({
            params: { receiptId },
          }).unwrap();

          refetch();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to approve receipt:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleReject = (receiptId: string) => {
    showConfirmationDialog(
      "رد فیش واریزی",
      "آیا از رد این فیش واریزی اطمینان دارید؟",
      "رد",
      "error",
      async () => {
        try {
          await rejectReceipt({
            params: { receiptId },
          }).unwrap();

          refetch();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to reject receipt:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleViewReceipt = (fileUrl: string) => {
    const fullFileUrl = `${import.meta.env.VITE_API_URL}${fileUrl}`;
    setViewingReceipt(fullFileUrl);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری لیست فیش‌های واریزی
      </Alert>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        مدیریت فیش‌های واریزی
      </Typography>

      <Paper sx={{ p: 3 }}>
        {/* Search */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="جستجو در نام یا شماره تلفن..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>کاربر</TableCell>
                <TableCell>شماره تلفن</TableCell>
                <TableCell>قیمت</TableCell>
                <TableCell>تاریخ آپلود</TableCell>
                <TableCell>فیش واریزی</TableCell>
                <TableCell>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow key={receipt._id}>
                  <TableCell>{receipt.userFullName}</TableCell>
                  <TableCell dir="ltr">{receipt.userPhoneNumber}</TableCell>
                  <TableCell>{receipt.price.toLocaleString()} تومان</TableCell>
                  <TableCell>
                    {new Date(receipt.createdAt).toLocaleDateString("fa-IR")}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewReceipt(receipt.fileUrl)}
                      color="primary"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={
                          <>
                            <CheckCircle className="icon-btn" />
                          </>
                        }
                        onClick={() => handleApprove(receipt._id)}
                      >
                        تأیید
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={
                          <>
                            <Cancel className="icon-btn" />
                          </>
                        }
                        onClick={() => handleReject(receipt._id)}
                      >
                        رد
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} از ${count !== -1 ? count : to}`
          }
        />

        {/* Receipt Viewer Dialog */}
        <Dialog
          open={Boolean(viewingReceipt)}
          onClose={() => setViewingReceipt("")}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>مشاهده فیش واریزی</DialogTitle>
          <DialogContent>
            {viewingReceipt && (
              <Box textAlign="center">
                <img
                  src={viewingReceipt}
                  alt="فیش واریزی"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewingReceipt("")}>بستن</Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <ConfirmationDialogComponent
          isOpen={confirmationDialog.isOpen}
          onClose={closeConfirmationDialog}
          onConfirm={confirmationDialog.onConfirm}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          confirmText={confirmationDialog.confirmText}
          confirmColor={confirmationDialog.confirmColor}
        />
      </Paper>
    </Box>
  );
};
